import React, { useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import {
  GENERIC_PAYMENT_ERROR,
  PAYMENT_METHODS,
  PAYMENT_PROVIDER,
} from '../../constants';
import { payCheck } from '../../../../store/slices/payment/paymentThunk';
import Check from '../check/check';
import StripeElement from '../../stripe/StripeElement';
import apiAccess from '../../../../api/apiAccess';
import { XpressBillPay } from '../../xpressbillpay';
import { Generic } from '../../generic';
import { PaymentFees, PaymentMethod } from 'src/types/payment';
import { useAppDispatch } from '../../../../store/store';
import { calculateTotalDue, isGeneric } from '../utils';

export interface PaymentFormProps {
  fees: PaymentFees;
  flow: string;
  onSuccess: (isGeneric?: boolean) => void;
  paymentID: string;
  paymentMethod: PaymentMethod;
  permitIdentifier: string;
  provider: PAYMENT_PROVIDER;
  signatoryName: string;
  userEmail: string;
}

export const PaymentForm = ({
  fees,
  flow,
  onSuccess,
  paymentID,
  paymentMethod,
  permitIdentifier,
  provider,
  signatoryName,
  userEmail,
}: PaymentFormProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const dispatch = useAppDispatch();

  const isGenericProvider = useMemo(() => isGeneric(provider), [provider]);

  const getPaymentApi = (
    paymentMethod: PaymentMethod,
    provider: PAYMENT_PROVIDER
  ) => {
    switch (paymentMethod) {
      case PAYMENT_METHODS.CHECK:
        return async () => dispatch(payCheck());
      case PAYMENT_METHODS.CREDIT_OR_DEBIT__CARD:
        if (provider === PAYMENT_PROVIDER.STRIPE) {
          return apiAccess.payByCreditCard;
        }
        return apiAccess.payGeneric;
      case PAYMENT_METHODS.ACH_DIRECT_DEBIT:
        if (provider === PAYMENT_PROVIDER.STRIPE) {
          return apiAccess.payByACHDirectDebit;
        }
        return apiAccess.payGeneric;
      default:
        return undefined;
    }
  };

  const totalDue = useMemo(
    () => calculateTotalDue(paymentMethod, fees, isGenericProvider),
    [fees, paymentMethod, isGenericProvider]
  );

  const handlePayment = async () => {
    setLoading(true);
    const paymentApi = getPaymentApi(paymentMethod, provider);
    try {
      if (paymentApi) {
        const response = await paymentApi(paymentID, signatoryName, userEmail);
        onSuccess(isGenericProvider && paymentMethod !== PAYMENT_METHODS.CHECK);
        return response;
      }
      setError(GENERIC_PAYMENT_ERROR);
    } catch (err) {
      setError(GENERIC_PAYMENT_ERROR);
      Sentry.captureException(err);
    } finally {
      setLoading(false);
    }
  };

  if (paymentMethod === PAYMENT_METHODS.CHECK) {
    return (
      <Check
        onPay={handlePayment}
        loading={loading}
        error={error}
      />
    );
  } else if (provider === PAYMENT_PROVIDER.STRIPE) {
    return (
      <StripeElement
        flow={flow}
        onPay={handlePayment}
        paymentMethod={paymentMethod}
      />
    );
  } else if (provider === PAYMENT_PROVIDER.XPRESS_BILL_PAY) {
    return (
      <XpressBillPay
        onPay={handlePayment}
        permitIdentifier={permitIdentifier}
        totalDue={totalDue}
        loading={loading}
        error={error}
      />
    );
  } else if (isGenericProvider) {
    return (
      <Generic
        onPay={handlePayment}
        totalDue={totalDue}
        loading={loading}
        error={error}
      />
    );
  }

  return <></>;
};
